import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { AUTHOR_URL, BOOK_URL, ORDER_TRACK_URL, CATEGORY_URL, PUBLICATION_URL, DASHBOARD_URL, ORDER_HISTORY_URL, REVIEW_URL } from '../../constants/routerUrl';
import {
  AccountCircle,
  LibraryBooks,
  MenuBook,
  Category,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  RateReview,
} from '@mui/icons-material';
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import { AuthContext } from '../../contexts/AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-force-tabpanel-${index}`}
      aria-labelledby={`scrollable-force-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `scrollable-force-tab-${index}`,
    'aria-controls': `scrollable-force-tabpanel-${index}`,
  };
}

export default function ScrollableTabsButtonForce() {
  const theme = useTheme();
  const location = useLocation();
  const auth = useContext(AuthContext);
  
  // Check if user is writer or publication
  const isWriterOrPublication = auth.isLoggedIn && auth.user && (auth.user.role === 'writer' || auth.user.role === 'publication');
  
  const getCurrentTabValue = useCallback(() => {
    if (location.pathname.startsWith(BOOK_URL)) return 0;
    if (location.pathname.startsWith(CATEGORY_URL)) return 1;
    if (location.pathname.startsWith(AUTHOR_URL)) return 2;
    if (location.pathname.startsWith(PUBLICATION_URL)) return 3;
    if (location.pathname.startsWith(ORDER_TRACK_URL)) return 4;
    if (location.pathname.startsWith(ORDER_HISTORY_URL)) return 5;
    if (location.pathname.startsWith(DASHBOARD_URL)) return 6;
    if (location.pathname.startsWith(REVIEW_URL)) return 7;
    return 0;
  }, [location.pathname]);

  const [value, setValue] = useState(getCurrentTabValue());

  useEffect(() => {
    setValue(getCurrentTabValue());
  }, [location.pathname, getCurrentTabValue]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Define colors for light and dark modes
  const backgroundColor = theme.palette.mode === 'dark' 
    ? theme.palette.background.paper 
    : theme.palette.secondary.main;
  
  const textColor = theme.palette.mode === 'dark' 
    ? theme.palette.text.primary 
    : theme.palette.primary.another;
  
  const indicatorColor = theme.palette.mode === 'dark' 
    ? theme.palette.secondary.main 
    : theme.palette.primary.another;

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: '100%',
        position: 'sticky',
        top: theme.spacing(7.5),
        [theme.breakpoints.down('sm')]: {
          top: theme.spacing(6.5),
        },
        zIndex: (theme) => theme.zIndex.drawer + 1,
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 2px 4px rgba(0,0,0,0.5)' 
          : '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <AppBar 
        position="static"   
        color="default"
        sx={{
          backgroundColor: backgroundColor,
          transition: 'background-color 0.3s ease',
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          indicatorColor="primary"
          textColor="inherit"
          aria-label="scrollable force tabs example"
          TabIndicatorProps={{
            style: {
              backgroundColor: indicatorColor,
              height: '4px',
            },
          }}
          sx={{
            '& .MuiTabs-flexContainer': {
              paddingLeft: { xs: theme.spacing(0), md: theme.spacing(16) },
            },
            '& .MuiTab-root': {
              minWidth: 150,
              pt: 0,
              pb: 0,
              color: textColor,
              opacity: 0.8,
              transition: 'all 0.3s ease',
              '&:hover': {
                opacity: 1,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-selected': {
                color: textColor,
                opacity: 1,
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab
            value={0}
            component={Link}
            to={BOOK_URL}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MenuBook fontSize="small" />{' '}
                <Typography variant="subtitle1" noWrap>
                  &nbsp;বই
                </Typography>
              </Box>
            }
            {...a11yProps(0)}
          />
          <Tab
            value={1}
            component={Link}
            to={CATEGORY_URL}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Category fontSize="small" />{' '}
                <Typography variant="subtitle1" noWrap>
                  &nbsp;বিষয়
                </Typography>
              </Box>
            }
            {...a11yProps(3)}
          />
          <Tab
            value={2}
            component={Link}
            to={AUTHOR_URL}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountCircle fontSize="small" />{' '}
                <Typography variant="subtitle1" noWrap>
                  &nbsp;লেখক
                </Typography>
              </Box>
            }
            {...a11yProps(1)}
          />
          <Tab
            value={3}
            component={Link}
            to={PUBLICATION_URL}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LibraryBooks fontSize="small" />{' '}
                <Typography variant="subtitle1" noWrap>
                  &nbsp;প্রকাশনী
                </Typography>
              </Box>
            }
            {...a11yProps(2)}
          />
          <Tab
            value={4}
            component={Link}
            to={ORDER_TRACK_URL}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ShareLocationIcon fontSize="small" />{' '}
                <Typography variant="subtitle1" noWrap>
                  &nbsp;অর্ডার ট্র্যাকিং
                </Typography>
              </Box>
            }
            {...a11yProps(4)}
          />
          {auth.isLoggedIn && (
            <Tab
              value={5}
              component={Link}
              to={ORDER_HISTORY_URL}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HistoryIcon fontSize="small" />{' '}
                  <Typography variant="subtitle1" noWrap>
                    &nbsp;অর্ডার হিস্টোরি
                  </Typography>
                </Box>
              }
              {...a11yProps(5)}
            />
          )}
          {isWriterOrPublication && (
            <Tab
              value={6}
              component={Link}
              to={DASHBOARD_URL}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DashboardIcon fontSize="small" />{' '}
                  <Typography variant="subtitle1" noWrap>
                    &nbsp;ড্যাশবোর্ড
                  </Typography>
                </Box>
              }
              {...a11yProps(6)}
            />
          )}
          <Tab
            value={7}
            component={Link}
            to={REVIEW_URL}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RateReview fontSize="small" />{' '}
                <Typography variant="subtitle1" noWrap>
                  &nbsp;বুক রিভিউ
                </Typography>
              </Box>
            }
            {...a11yProps(6)}
          />
        </Tabs>
      </AppBar>
    </Box>
  );
}
